<script lang="ts">
  import { Button, buttonVariants } from "$lib/components/ui/button/index.js";
  import * as Dialog from "$lib/components/ui/dialog/index.js";
  import { Input } from "$lib/components/ui/input/index.js";
  import { Label } from "$lib/components/ui/label/index.js";
  import { t } from "svelte-i18n";
  import * as Tooltip from "$lib/components/ui/tooltip/index.js";
  import { ArrowLeftRight } from "lucide-svelte";
  import { type CustomerSummary } from "$src/domain/customers/CustomerSummary";
  import AddressValue from "$src/presentation/core/address-value.svelte";
  import TimeSince from "$src/presentation/core/time-since.svelte";
  import { getCustomerInfo } from "$src/domain/customers/utils/customer-info-utils";
  import SelectCustomer from "./select-customer.svelte";
  import { type StoreSummary } from "$src/domain/directory/stores/StoreSummary";
  import * as zod from "zod";
  import { validator } from "@felte/validator-zod";
  import { createForm } from "felte";
  import { createEventDispatcher } from "svelte";

  export let groupStoreId: number;
  export let selectedStore: StoreSummary | undefined;

  let selectedCustomer: CustomerSummary | undefined;

  function customerSelected(event: CustomEvent<CustomerSummary>) {
    selectedCustomer = event.detail;
    $data.customerId = selectedCustomer.id;
  }

  const schema = zod.object({
    customerId: zod.number({
      required_error: $t("page.projects.new.customer-required"),
      invalid_type_error: $t("page.projects.new.customer-required"),
    }),
  });

  type FormValues = zod.infer<typeof schema>;

  $: editForm = createForm({
    initialValues: {},
    onSubmit: handleSubmit,
    extend: validator({ schema: schema }),
  });

  $: form = editForm.form;
  $: data = editForm.data;
  $: errors = editForm.errors;

  const dispatch = createEventDispatcher();

  function handleSubmit(values: FormValues) 
  {
    dispatch("selectCustomer", selectedCustomer);
    open = false;
  }

  let open: boolean;

  function onOpenChange(isOpen: boolean) {
    if( isOpen )
      selectedCustomer = undefined
  }

</script>

<Dialog.Root bind:open={open} onOpenChange={onOpenChange}>
  <Dialog.Trigger>
    <Tooltip.Root>
      <Tooltip.Trigger asChild let:builder>
        <Button
          builders={[builder]}
          size="miniAction"
          class="rounded-full  transition-transform transform  hover:scale-110"
        >
          <ArrowLeftRight size="20" />
        </Button>
      </Tooltip.Trigger>
      <Tooltip.Content>
        <p>{$t("page.projects.detail.change-customer")}</p>
      </Tooltip.Content>
    </Tooltip.Root>
  </Dialog.Trigger>
  <Dialog.Content class="sm:max-w-[425px]">
    <Dialog.Header>
      <Dialog.Title>
        {$t("page.projects.detail.change-customer")}
      </Dialog.Title>
    </Dialog.Header>
    <form use:form>
      <div class="grid gap-4 py-4">
        <div class="flex flex-col gap-2">
          <SelectCustomer
            {groupStoreId}
            defaultCountryCode={selectedStore?.countryCode}
            on:selectCustomer={customerSelected}
          />
          {#if selectedCustomer}
            <div class="flex-1 space-y-1 border rounded-lg p-4">
              <div class="flex w-full flex-col gap-1">
                <div class="flex items-center">
                  <div class="flex items-center gap-2">
                    <div class="font-semibold">
                      {getCustomerInfo(selectedCustomer)}
                    </div>
                  </div>
                  <div class="ml-auto text-xs text-muted-foreground">
                    <TimeSince dateString={selectedCustomer.lastProjectDate} />
                  </div>
                </div>
              </div>
              <div class="flex w-full flex-col gap-1">
                <div class="flex items-center">
                  <div class="line-clamp-2 text-xs text-muted-foreground">
                    <AddressValue value={selectedCustomer.billingAddress} />
                  </div>
                  <div class="ml-auto text-xs text-muted-foreground">
                    {selectedCustomer.orderCount ?? 0}
                    {$t("page.search.results.sales")}
                  </div>
                </div>
              </div>
            </div>
          {/if}
          {#if $errors.customerId}
            <div class="col-span-4 text-right text-xs text-red-500">
              {$errors.customerId}
            </div>
          {/if}
        </div>
      </div>
      <Dialog.Footer>
        <Button type="submit" class="px-8">
          {$t("global.messages.ok")}
        </Button>
      </Dialog.Footer>
    </form>
  </Dialog.Content>
</Dialog.Root>
